import { useEffect, useMemo, useState } from 'react';
import {
  Box, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Tab, Tabs, Typography, Table, TableBody,
  TableCell, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, Cell,
} from 'recharts';
import { liveMatchService } from '../../../api/service.ts';
import type { LiveGameEvent } from '../liveMatchTypes.ts';
import {
  computeSetProgression, computeServeStats, computeRuns,
  computeTimeoutEffects, computeRotationStats, computeKeyMoments,
} from './matchStats.ts';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: number;
  homeTeamName: string;
  awayTeamName: string;
  homeShirtColor?: string;
  awayShirtColor?: string;
}

// ─── Costanti colori ──────────────────────────────────────────────────────────

const TAB_LABELS = ['Andamento', 'Servizio', 'Parziali', 'Timeout', 'Rotazioni', 'Momenti chiave'];

const DARK_BG   = '#0d1117';
const HEADER_BG = '#0d0d1a';
const PANEL_BG  = 'rgba(255,255,255,0.04)';
const TEXT      = 'rgba(255,255,255,0.87)';
const TEXT_DIM  = 'rgba(255,255,255,0.45)';

// ─── Componente principale ────────────────────────────────────────────────────

export default function MatchStatsDialog({
  open, onClose, eventId,
  homeTeamName, awayTeamName,
  homeShirtColor = '#1565C0',
  awayShirtColor = '#B71C1C',
}: Props) {
  const [events, setEvents]         = useState<LiveGameEvent[]>([]);
  const [ourTeamSide, setOurTeamSide] = useState<'HOME' | 'AWAY'>('HOME');
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState(0);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      liveMatchService.getEventLog(eventId),
      liveMatchService.getCallupData(eventId),
    ]).then(([evts, cd]) => {
      setEvents(Array.isArray(evts) ? evts : []);
      setOurTeamSide(cd?.ourTeamSide ?? 'HOME');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open, eventId]);

  const progression     = useMemo(() => computeSetProgression(events),    [events]);
  const serveStats      = useMemo(() => computeServeStats(events),         [events]);
  const runs            = useMemo(() => computeRuns(events),               [events]);
  const timeoutEffects  = useMemo(() => computeTimeoutEffects(events),     [events]);
  const rotationStats   = useMemo(() => computeRotationStats(events),      [events]);
  const keyMoments      = useMemo(() => computeKeyMoments(events),         [events]);

  // Se VbVolley ha giocato come AWAY, i dati "home" nel log sono dell'avversario.
  // Scambiamo nomi e colori per mostrare sempre VbVolley vs Avversario correttamente.
  const swapped     = ourTeamSide === 'AWAY';
  const homeDisplay = swapped ? awayTeamName : homeTeamName;
  const awayDisplay = swapped ? homeTeamName : awayTeamName;

  const HOME_COLOR  = swapped ? awayShirtColor : homeShirtColor;
  const AWAY_COLOR  = swapped ? homeShirtColor : awayShirtColor;
  const HOME_LIGHT  = `${HOME_COLOR}99`;
  const AWAY_LIGHT  = `${AWAY_COLOR}99`;

  // ─── Serve chart data ─────────────────────────────────────────────────────

  const serveChartData = serveStats.map((s) => ({
    name: `Set ${s.setNumber}`,
    [`${homeDisplay} serv.`]: s.homeServePoints,
    [`${homeDisplay} ric.`]:  s.homeBreakPoints,
    [`${awayDisplay} serv.`]: s.awayServePoints,
    [`${awayDisplay} ric.`]:  s.awayBreakPoints,
  }));

  // ─── Rotation chart data ──────────────────────────────────────────────────

  const rotChartData = rotationStats.map((r) => ({
    name: `Pos ${r.setterPosition}`,
    [homeDisplay]: r.homePoints,
    [awayDisplay]: r.awayPoints,
  }));

  // ─── Runs chart data ──────────────────────────────────────────────────────

  const runsChartData = runs.map((r, i) => ({
    name: `S${r.setNumber} #${i + 1}`,
    Lunghezza: r.length,
    team: r.team,
    score: `${r.startScore.home}-${r.startScore.away}`,
  }));

  // ─── Tooltip personalizzato per andamento ─────────────────────────────────

  const ProgressionTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{ bgcolor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1, p: 1 }}>
        <Typography sx={{ color: TEXT_DIM, fontSize: 11 }}>Rally {label}</Typography>
        {payload.map((p: any) => (
          <Typography key={p.dataKey} sx={{ color: p.color, fontSize: 12, fontWeight: 600 }}>
            {p.name}: {p.value}
          </Typography>
        ))}
      </Box>
    );
  };

  // ─── Render tab content ───────────────────────────────────────────────────

  const renderAndamento = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {progression.length === 0 && <Typography sx={{ color: TEXT_DIM }}>Nessun dato disponibile.</Typography>}
      {progression.map((sp) => (
        <Box key={sp.setNumber} sx={{ bgcolor: PANEL_BG, borderRadius: 2, p: 2 }}>
          <Typography sx={{ color: TEXT, fontWeight: 600, mb: 1.5, fontSize: 13 }}>Set {sp.setNumber}</Typography>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={sp.points} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="rally" tick={{ fill: TEXT_DIM, fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: TEXT_DIM, fontSize: 10 }} />
              <Tooltip content={<ProgressionTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: TEXT_DIM }} />
              <Line type="monotone" dataKey="home" name={homeDisplay} stroke={HOME_COLOR} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="away" name={awayDisplay} stroke={AWAY_COLOR} dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ))}
    </Box>
  );

  const renderServizio = () => (
    <Box sx={{ bgcolor: PANEL_BG, borderRadius: 2, p: 2 }}>
      <Typography sx={{ color: TEXT_DIM, fontSize: 11, mb: 2 }}>
        Punti al servizio (serv.) e in ricezione (ric.) per set
      </Typography>
      {serveChartData.length === 0 && <Typography sx={{ color: TEXT_DIM }}>Nessun dato disponibile.</Typography>}
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={serveChartData} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey="name" tick={{ fill: TEXT_DIM, fontSize: 11 }} />
          <YAxis tick={{ fill: TEXT_DIM, fontSize: 10 }} />
          <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6 }} labelStyle={{ color: TEXT_DIM }} />
          <Legend wrapperStyle={{ fontSize: 11, color: TEXT_DIM }} />
          <Bar dataKey={`${homeDisplay} serv.`} fill={HOME_COLOR}  maxBarSize={28} />
          <Bar dataKey={`${homeDisplay} ric.`}  fill={HOME_LIGHT}  maxBarSize={28} />
          <Bar dataKey={`${awayDisplay} serv.`} fill={AWAY_COLOR}  maxBarSize={28} />
          <Bar dataKey={`${awayDisplay} ric.`}  fill={AWAY_LIGHT}  maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  const renderParziali = () => (
    <Box sx={{ bgcolor: PANEL_BG, borderRadius: 2, p: 2 }}>
      <Typography sx={{ color: TEXT_DIM, fontSize: 11, mb: 2 }}>
        Parziali consecutivi ≥ 3 punti
      </Typography>
      {runsChartData.length === 0 && <Typography sx={{ color: TEXT_DIM }}>Nessun parziale significativo.</Typography>}
      <ResponsiveContainer width="100%" height={Math.max(160, runsChartData.length * 36)}>
        <BarChart data={runsChartData} layout="vertical" margin={{ top: 0, right: 40, left: 50, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" horizontal={false} />
          <XAxis type="number" tick={{ fill: TEXT_DIM, fontSize: 10 }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fill: TEXT_DIM, fontSize: 10 }} width={48} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6 }}
            labelStyle={{ color: TEXT_DIM }}
            formatter={(value: any, _: any, props: any) => [
              `${value} punti dal ${props.payload.score}`,
              props.payload.team === 'HOME' ? homeDisplay : awayDisplay,
            ]}
          />
          <Bar dataKey="Lunghezza" maxBarSize={22} label={{ position: 'right', fill: TEXT, fontSize: 11 }}>
            {runsChartData.map((entry, i) => (
              <Cell key={i} fill={entry.team === 'HOME' ? HOME_COLOR : AWAY_COLOR} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  const renderTimeout = () => (
    <Box sx={{ bgcolor: PANEL_BG, borderRadius: 2, p: 2, overflowX: 'auto' }}>
      {timeoutEffects.length === 0 && <Typography sx={{ color: TEXT_DIM }}>Nessun timeout registrato.</Typography>}
      {timeoutEffects.length > 0 && (
        <Table size="small" sx={{ minWidth: 380 }}>
          <TableHead>
            <TableRow>
              {['Set', 'Squadra', 'Punteggio', 'Prossimi 3 punti', 'Efficace'].map((h) => (
                <TableCell key={h} sx={{ color: TEXT_DIM, fontSize: 11, borderColor: 'rgba(255,255,255,0.1)', pb: 0.5 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeoutEffects.map((t, i) => {
              const teamName = t.callingTeam === 'HOME' ? homeDisplay : awayDisplay;
              const teamColor = t.callingTeam === 'HOME' ? HOME_COLOR : AWAY_COLOR;
              return (
                <TableRow key={i} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.07)', py: 0.75 } }}>
                  <TableCell sx={{ color: TEXT_DIM, fontSize: 12 }}>{t.setNumber}</TableCell>
                  <TableCell sx={{ color: teamColor, fontSize: 12, fontWeight: 600 }}>{teamName}</TableCell>
                  <TableCell sx={{ color: TEXT, fontSize: 12 }}>{t.scoreBefore.home}–{t.scoreBefore.away}</TableCell>
                  <TableCell sx={{ color: TEXT, fontSize: 12 }}>
                    <span style={{ color: HOME_COLOR }}>{t.pointsAfter.home}</span>
                    {' – '}
                    <span style={{ color: AWAY_COLOR }}>{t.pointsAfter.away}</span>
                  </TableCell>
                  <TableCell>
                    {t.wasEffective
                      ? <CheckIcon sx={{ color: '#66bb6a', fontSize: 18 }} />
                      : <CloseRoundedIcon sx={{ color: '#ef5350', fontSize: 18 }} />}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  const renderRotazioni = () => (
    <Box sx={{ bgcolor: PANEL_BG, borderRadius: 2, p: 2 }}>
      <Typography sx={{ color: TEXT_DIM, fontSize: 11, mb: 2 }}>
        Punti per posizione del palleggiatore ({homeDisplay})
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={rotChartData} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey="name" tick={{ fill: TEXT_DIM, fontSize: 11 }} />
          <YAxis tick={{ fill: TEXT_DIM, fontSize: 10 }} allowDecimals={false} />
          <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6 }} labelStyle={{ color: TEXT_DIM }} />
          <Legend wrapperStyle={{ fontSize: 11, color: TEXT_DIM }} />
          <Bar dataKey={homeDisplay} fill={HOME_COLOR} maxBarSize={32} />
          <Bar dataKey={awayDisplay} fill={AWAY_COLOR} maxBarSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  const MOMENT_ICON: Record<string, string> = { MAX_LEAD: '📈', COMEBACK: '🔄', TIGHT_SET: '⚡' };

  const renderMomenti = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {keyMoments.length === 0 && <Typography sx={{ color: TEXT_DIM }}>Nessun momento chiave rilevato.</Typography>}
      {keyMoments.map((m, i) => {
        const teamColor = m.team === 'HOME' ? HOME_COLOR : AWAY_COLOR;
        const teamName  = m.team === 'HOME' ? homeDisplay : awayDisplay;
        return (
          <Box key={i} sx={{ bgcolor: PANEL_BG, borderRadius: 2, px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{MOMENT_ICON[m.type]}</Typography>
            <Box>
              <Typography sx={{ color: TEXT_DIM, fontSize: 10, letterSpacing: 0.5 }}>Set {m.setNumber}</Typography>
              <Typography sx={{ color: teamColor, fontWeight: 700, fontSize: 13 }}>{teamName}</Typography>
              <Typography sx={{ color: TEXT, fontSize: 12 }}>{m.description}</Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const TABS_CONTENT = [renderAndamento, renderServizio, renderParziali, renderTimeout, renderRotazioni, renderMomenti];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ bgcolor: HEADER_BG, color: 'white', py: 1, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
          Statistiche — {homeDisplay} vs {awayDisplay}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          bgcolor: HEADER_BG, flexShrink: 0,
          '& .MuiTab-root': { color: TEXT_DIM, fontSize: 11, minWidth: 80, py: 1 },
          '& .Mui-selected': { color: '#FFD700 !important' },
          '& .MuiTabs-indicator': { bgcolor: '#FFD700' },
        }}
      >
        {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      <DialogContent sx={{ bgcolor: DARK_BG, p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1.5, sm: 2.5 } }}>
            {TABS_CONTENT[tab]()}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
